import React, { Fragment, useEffect, useState } from "react"
import { Box, Button, Card, Divider, Grid2 as Grid, Stack, Typography } from "@mui/material"
import { GenericMuiDatagrid } from "../../../components/generic/GenericDatagrid"
import { GenericAvatar } from "../../../components/generic/GenericAvatar";
import { formatDate } from "../../../helpers/dateHelper";
import { PENDING, STATUS_COLORS } from "../../../enums/statusEnums";
import { acceptCollabRequest, cancelCollabRequest, getCollabRequest, rejectCollabRequest } from "../../../config/apisauce";
import { ASC, DESC, INITIAL_GET_PARAMS } from "../../../enums/generalEnums";
import { DAY_ONLY_FORMAT } from "../../../enums/dateEnums";
import { CollabRequestDialog } from "../../shared/CollabRequestDialog";
import { GenericNoData } from "../../../components/generic/GenericNoData";
import { Envelope, SortAscending, SortDescending } from "@phosphor-icons/react";
import useStore from "../../../states/store";
import useNotiStack from "../../../hooks/useNotiStack";
import LoadingButton from "@mui/lab/LoadingButton";
import { CollabRequestUpdateDialog } from "../../shared/CollabRequestUpdateDialog";
import { router } from "@inertiajs/react";
import useDebounce from "../../../hooks/useDebounce";
import { GenericIconButton } from "../../../components/generic/GenericIconButton";

export const CollabRequests = (props) => {
  const {
    showHeader = true,
    hideFooter,
    additionalColumns = [],
    tableHeight,
    additionalProps = {},
    viewOnly = false,
    isCollabRequest = true,
    title = 'Collab Requests',
    subTitle = 'Track the requests of your users.'
  } = props;
  ;
  const { success } = useNotiStack();
  const [{ inProgressIds }, { openLoader, closeLoader }] = useStore();
  const [openCollabRequestDialog, setOpenCollabRequestDialog] = useState(false);
  const [collabRequests, setCollabRequests] = useState([]);
  const [selectedCollabRequest, setSelectedCollabRequest] = useState({});
  const [pagination, setPagination] = useState({ pageSize: additionalProps?.per_page ?? 5, page: 0 });
  const [filters, setFilters] = useState({ ...INITIAL_GET_PARAMS, ...additionalProps });
  const [openCollabRequestUpdateDialog, setOpenCollabRequestUpdateDialog] = useState(false);

  const genericColumnProps = {
    editable: false,
    sortable: false,
    resizable: false
  };

  const fetchCollabRequest = async (params) => {
    openLoader();
    const response = await getCollabRequest({
      ...params,
      ...filters,
      per_page: pagination?.pageSize,
      page: pagination?.page + 1
    });
    if (response.ok) {
      setCollabRequests(response?.data);
    }
    closeLoader();
  };

  const handleOpenCollabRequestDialog = (data) => {
    if (isCollabRequest) {
      setOpenCollabRequestDialog(true);
    } else {
      setOpenCollabRequestUpdateDialog(true);
    }
    setSelectedCollabRequest(data);
  };

  const handleAcceptRejectCollabRequest = async (values, isAccept) => {
    openLoader(values?.id);
    const params = {
      hash: values?.fields?.hash
    };
    const response = isAccept ? await acceptCollabRequest(params) : await rejectCollabRequest(params);
    if (response.ok) {
      fetchCollabRequest();
      isAccept ? success('Accepted collab request!') : success('Rejected collab request!');
    }
    closeLoader(values?.id);
  };

  const handleCancelRequest = async (values) => {
    openLoader(values?.id);
    const response = await cancelCollabRequest({ hash: values?.fields?.hash });
    if (response.ok) {
      fetchCollabRequest();
      success('Cancelled collab request');
    }
    closeLoader(values?.id);
  };

  const handleFilterChange = useDebounce((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    fetchCollabRequest({ [key]: value });
  }, 500);

  const columns = [
    ...additionalColumns,
    {
      field: 'name',
      headerName: 'Name',
      flex: 1.2,
      renderCell: ({ row }) => {
        const requestDetails = isCollabRequest ? row?.requested_by?.details : row?.requested_to?.details;
        return (
          <Stack direction={'row'} alignItems={'center'} gap={1.5} mt={0.5}>
            <GenericAvatar location={requestDetails?.profile_picture?.location} avatarId={row?.id} size={'42px'} backgroundColor={'background.default'} />
            <Typography fontWeight={700}>{isCollabRequest ? row?.requested_by?.name : row?.requested_to?.name}</Typography>
          </Stack>
        )
      },
      ...genericColumnProps
    },
    {
      field: 'profession',
      headerName: 'Profession',
      flex: 1,
      renderCell: ({ row }) => row?.requested_by?.details?.profession ?? 'None',
      ...genericColumnProps
    },
    {
      field: 'created_at',
      headerName: 'Request Date',
      flex: 1,
      renderCell: ({ row }) => formatDate(new Date(row?.created_at)),
      ...genericColumnProps
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      renderCell: ({ row }) => {
        return (
          <Typography mt={1.7} fontWeight={700} textTransform={'capitalize'} color={STATUS_COLORS[row?.status]}>{row?.status}</Typography>
        )
      },
      ...genericColumnProps
    },
    {
      field: 'details',
      headerName: viewOnly ? 'Action' : 'Details',
      flex: 1,
      renderCell: ({ row }) => {
        return (
          <Box>
            {(row?.status !== PENDING || isCollabRequest) && <Button fullWidth variant="contained" onClick={() => handleOpenCollabRequestDialog(row)}>Details</Button>}
            {(row?.status === PENDING && !isCollabRequest) && <Button fullWidth variant="outlined" onClick={() => handleCancelRequest(row)} disabled={inProgressIds?.includes(row?.id)}>Cancel Request</Button>}
          </Box>
        )
      },
      ...genericColumnProps
    },
    !viewOnly && {
      field: 'action',
      headerName: 'Action',
      flex: 2,
      renderCell: ({ row }) => {
        const processing = inProgressIds?.includes(row?.id);
        return (
          <Stack gap={0.5} direction={'row'} mt={1.1}>
            <LoadingButton
              fullWidth
              onClick={() => handleAcceptRejectCollabRequest(row, false)}
              disabled={processing}
              variant="contained"
              color="error"
            >
              Reject
            </LoadingButton>
            <LoadingButton
              fullWidth
              onClick={() => handleAcceptRejectCollabRequest(row, true)}
              disabled={processing}
              variant="contained"
              color="success"
            >
              Accept
            </LoadingButton>
          </Stack>
        );
      },
      ...genericColumnProps
    }
  ].filter(Boolean);

  useEffect(() => {
    fetchCollabRequest({ ...filters, ...pagination });
  }, [pagination, filters])

  return (
    <Card sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflowY: 'auto' }}>
      <Grid container spacing={1.5}>
        <Grid item size={{ xs: showHeader ? 12 : 8, sm: 6 }}>
          <Typography variant="h6" fontWeight={700} color="primary" noWrap>{title}</Typography>
        </Grid>
        <Grid container item justifyContent={'flex-end'} size={{ xs: showHeader ? 12 : 4, sm: 6 }}>
          {
            showHeader ? <Button variant="outlined" sx={{ width: { xs: '100%', sm: '102px' } }} onClick={() => router.visit('/admin-collab-requests')}>See All</Button>
              : <GenericIconButton
                onClick={() => handleFilterChange('sort_by', filters?.sort_by === ASC ? DESC : ASC)}
                tooltip={`Sort By: ${filters?.sort_by === ASC ? 'Descending' : 'Ascending'}`}
                color={'primary.main'}
                icon={filters?.sort_by === ASC ? <SortAscending size={24} /> : <SortDescending size={24} />}
              />
          }
        </Grid>
      </Grid>
      <Typography fontWeight={500} mb={2}>{subTitle}</Typography>
      {
        collabRequests?.data?.length > 0 ? <GenericMuiDatagrid
          {...collabRequests}
          hideFooter={hideFooter}
          columns={columns}
          height={tableHeight}
          setPagination={setPagination}
        >
          {
            collabRequests?.data?.map((request, index) => {
              const requestDetails = isCollabRequest ? request?.requested_by?.details : request?.requested_to?.details;
              return (
                <Fragment key={`request-key-${index}`} >
                  <Box sx={{ padding: '16px' }}>
                    <Grid container>
                      <Grid item size={4}>
                        <GenericAvatar location={requestDetails?.profile_picture?.location} size={'92px'} backgroundColor={'background.default'} />
                      </Grid>
                      <Grid item size={8}>
                        <Stack direction={'row'} justifyContent={'space-between'}>
                          <Typography fontWeight={700} noWrap width={{ xs: 140, sm: '100%' }}>{request?.requested_by?.name}</Typography>
                          <Typography color="text.light">{formatDate(new Date(request?.created_at), DAY_ONLY_FORMAT)}</Typography>
                        </Stack>
                        <Stack>
                          <Typography noWrap>{requestDetails?.profession ?? 'None'}</Typography>
                          <Typography fontWeight={700} textTransform={'capitalize'} color={STATUS_COLORS[request?.requested_by?.status]}>{request?.requested_by?.status}</Typography>
                          {(request?.status !== PENDING || isCollabRequest) && <Button variant="contained" sx={{ mt: 1 }} onClick={() => handleOpenCollabRequestDialog(request)}>Details</Button>}
                          {(request?.status === PENDING && !isCollabRequest) && <Button fullWidth variant="outlined" sx={{ mt: 1 }} onClick={() => handleCancelRequest(request)} disabled={inProgressIds?.includes(request?.id)}>Cancel Request</Button>}
                        </Stack>
                      </Grid>
                    </Grid>
                  </Box>
                  {index !== (collabRequests?.data?.length - 1) && <Divider sx={{ borderColor: 'borders.main' }} />}
                </Fragment>
              )
            })
          }
        </GenericMuiDatagrid> : <GenericNoData height={"100%"} icon={<Envelope size={40} />} label={'No collab requests'} />
      }
      <CollabRequestDialog
        viewOnly={viewOnly}
        isTable={true}
        setCollabRequestList={setCollabRequests}
        collabRequestDetails={selectedCollabRequest}
        openCollabRequestDialog={openCollabRequestDialog}
        handleCloseRequestDialog={() => setOpenCollabRequestDialog(false)}
        refetchData={() => fetchCollabRequest()}
      />
      <CollabRequestUpdateDialog
        isCollabRequest={isCollabRequest}
        openCollabRequestUpdateDialog={openCollabRequestUpdateDialog}
        handleCloseRequestUpdateDialog={() => setOpenCollabRequestUpdateDialog(false)}
        collabRequestDetails={selectedCollabRequest}
      />
    </Card>
  );
};